<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useForm } from 'vee-validate';
import { toTypedSchema } from '@vee-validate/zod';
import BaseSelect from './BaseSelect.vue';
import BaseButton from './BaseButton.vue';
import { useModalStore, ModalType } from '@/stores/modal.ts';
import type { SelectOption } from './BaseSelect.vue';
import type { RoomOccupancy, Room, Person } from '@/types/SearchBar.ts';
import { RoomOccupancySchema } from '@/types/schemas/ui.ts';
import { $t } from '@/composables/translations/useTranslate.ts';

interface Props {
  modelValue: RoomOccupancy;
  maxRooms?: number;
  maxAdultsPerRoom?: number;
  maxChildrenPerRoom?: number;
  maxChildAge?: number;
  disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  maxRooms: 4,
  maxAdultsPerRoom: 4,
  maxChildrenPerRoom: 4,
  maxChildAge: 17,
  disabled: false,
});

const emit = defineEmits<{
  'update:modelValue': [value: RoomOccupancy];
  confirm: [];
  'auto-save': [value: RoomOccupancy];
  'more-options-click': [];
}>();

// Form setup
const { values, setFieldValue } = useForm({
  validationSchema: toTypedSchema(RoomOccupancySchema),
  initialValues: props.modelValue,
});

const customError = ref<string | null>(null);
const modalStore = useModalStore();

watch(
  () => props.modelValue,
  (newValue) => {
    if (!newValue.rooms || newValue.rooms.length === 0) {
      const defaultRoom: Room = {
        persons: [{ type: 'adult' }, { type: 'adult' }],
      };
      const defaultValue = { rooms: [defaultRoom] };

      Object.keys(defaultValue).forEach((key) => {
        setFieldValue(key as keyof RoomOccupancy, defaultValue[key as keyof RoomOccupancy]);
      });

      emit('update:modelValue', defaultValue);
    } else {
      Object.keys(newValue).forEach((key) => {
        setFieldValue(key as keyof RoomOccupancy, newValue[key as keyof RoomOccupancy]);
      });
    }
  },
  { immediate: true, deep: true }
);

const adultsOptions = computed((): SelectOption[] => {
  return Array.from({ length: props.maxAdultsPerRoom }, (_, i) => ({
    value: i + 1,
    label: `${i + 1} ${i === 0 ? $t('ibe.common.adult', 'Adult') : $t('ibe.common.adults', 'Adults')}`,
  }));
});

const childrenOptions = computed((): SelectOption[] => {
  const options: SelectOption[] = [
    {
      value: 0,
      label: $t('ibe.room-selector.no-children', 'No children'),
    },
  ];
  for (let i = 1; i <= props.maxChildrenPerRoom; i++) {
    options.push({
      value: i,
      label: `${i} ${i === 1 ? $t('ibe.common.child', 'Child') : $t('ibe.common.children', 'Children')}`,
    });
  }
  return options;
});

const childAgeOptions = computed((): SelectOption[] => {
  const options: SelectOption[] = [
    {
      value: 1,
      label: '0-1',
    },
  ];

  for (let i = 2; i <= props.maxChildAge; i++) {
    options.push({
      value: i,
      label: i.toString(),
    });
  }

  return options;
});

// Computed properties
const canDeleteRoom = computed(() => (values.rooms?.length || 0) > 1);
const canAddRoom = computed(() => (values.rooms?.length || 0) < props.maxRooms);

const getAdultCount = (room: Room) => {
  return room.persons.filter((p) => p.type === 'adult').length;
};

const getChildrenCount = (room: Room) => {
  return room.persons.filter((p) => p.type === 'child').length;
};

const getChildren = (room: Room) => {
  return room.persons.filter((p): p is Person & { type: 'child' } => p.type === 'child');
};

const updateRoomData = async (updatedRooms: Room[]) => {
  await setFieldValue('rooms', updatedRooms);
  customError.value = null;
};

const createPersonsArray = (
  adultsCount: number,
  existingChildren: Person[] = [],
  newChildrenCount: number = 0
): Person[] => {
  const adults: Person[] = Array.from({ length: adultsCount }, () => ({ type: 'adult' as const }));

  const children: Person[] = [];
  for (let i = 0; i < newChildrenCount; i++) {
    if (i < existingChildren.length) {
      children.push(existingChildren[i]);
    } else {
      children.push({ type: 'child' as const });
    }
  }

  return [...adults, ...children];
};

// Event handlers
const handleAddRoom = async () => {
  if (!canAddRoom.value || !values.rooms) return;

  const newRoom: Room = {
    persons: createPersonsArray(2),
  };

  const updatedRooms = [...values.rooms, newRoom];
  await updateRoomData(updatedRooms);
};

const handleRemoveRoom = async (roomIndex: number) => {
  if (!canDeleteRoom.value || !values.rooms) return;

  const updatedRooms = values.rooms.filter((_, index) => index !== roomIndex);
  await updateRoomData(updatedRooms);
};

const handleAdultsChange = async (roomIndex: number, count: number) => {
  if (!values.rooms || roomIndex < 0 || roomIndex >= values.rooms.length) return;

  const currentRoom = values.rooms[roomIndex];
  const existingChildren = currentRoom.persons.filter((p) => p.type === 'child');

  const newPersons = createPersonsArray(count, existingChildren, existingChildren.length);

  const updatedRooms = [...values.rooms];
  updatedRooms[roomIndex] = { ...currentRoom, persons: newPersons };

  await updateRoomData(updatedRooms);
};

const handleChildrenChange = async (roomIndex: number, count: number) => {
  if (!values.rooms || roomIndex < 0 || roomIndex >= values.rooms.length) return;

  const currentRoom = values.rooms[roomIndex];
  const adultCount = currentRoom.persons.filter((p) => p.type === 'adult').length;
  const existingChildren = currentRoom.persons.filter((p) => p.type === 'child');

  const newPersons = createPersonsArray(adultCount, existingChildren, count);

  const updatedRooms = [...values.rooms];
  updatedRooms[roomIndex] = { ...currentRoom, persons: newPersons };

  await updateRoomData(updatedRooms);
};

const handleChildAgeChange = async (roomIndex: number, childIndex: number, age: number | null) => {
  if (age === null || !values.rooms || roomIndex < 0 || roomIndex >= values.rooms.length) return;

  const currentRoom = values.rooms[roomIndex];
  const adults = currentRoom.persons.filter((p) => p.type === 'adult');
  const children = currentRoom.persons.filter((p) => p.type === 'child');

  if (childIndex < children.length) {
    children[childIndex] = { type: 'child' as const, age };
  }

  const newPersons = [...adults, ...children];

  const updatedRooms = [...values.rooms];
  updatedRooms[roomIndex] = { ...currentRoom, persons: newPersons };

  await updateRoomData(updatedRooms);
};

const validateForConfirmation = (): string | null => {
  if (!values.rooms) return null;

  for (const room of values.rooms) {
    const children = room.persons.filter((p) => p.type === 'child');
    for (const child of children) {
      if (child.age === undefined || child.age === null) {
        return $t(
          'ibe.room-selector.age-required',
          'Please provide the ages of your children at the time of departure.'
        );
      }
    }
  }
  return null;
};

const handleConfirm = () => {
  const validationError = validateForConfirmation();
  if (validationError) {
    customError.value = validationError;
    return;
  }

  customError.value = null;
  if (values.rooms) {
    emit('update:modelValue', { rooms: [...values.rooms] });
  }
  emit('confirm');
};

const handleAutoSave = () => {
  const validationError = validateForConfirmation();
  if (!validationError && values.rooms && values.rooms.length > 0) {
    const roomOccupancy = { rooms: [...values.rooms] };
    emit('update:modelValue', roomOccupancy);
    emit('auto-save', roomOccupancy);
  }
};

defineExpose({
  handleAutoSave,
});
</script>

<template>
  <div class="w-[315px] bg-white">
    <!-- Rooms list -->
    <div>
      <div
        v-for="(room, roomIndex) in values.rooms || []"
        :key="roomIndex"
        class="border-b mb-5 border-gray-200 pb-5 last:border-0 last:mb-0"
      >
        <!-- Room header -->
        <div class="flex items-center justify-between pb-3">
          <span class="font-semibold text-text-primary">
            {{ $t('ibe.room-selector.room-number', 'Room {number}', { number: roomIndex + 1 }) }}
          </span>
          <button
            v-if="canDeleteRoom"
            type="button"
            class="flex hover:opacity-50 transition-opacity"
            @click="handleRemoveRoom(roomIndex)"
          >
            <span
              class="w-4 h-4 bg-text-primary icon icon-x"
              :aria-label="$t('ibe.room-selector.delete-room', 'delete')"
            ></span>
          </button>
        </div>

        <!-- Room content -->
        <div>
          <!-- Adults dropdown -->
          <div class="mb-5">
            <label
              :for="`adults-${roomIndex}`"
              class="block text-sm font-medium text-gray-700 mb-2"
            >
              {{ $t('ibe.common.adults', 'Adults') }}
            </label>
            <BaseSelect
              :id="`adults-${roomIndex}`"
              :model-value="getAdultCount(room)"
              :options="adultsOptions"
              :disabled="disabled"
              wrapper-class="w-full"
              @update:model-value="(value) => handleAdultsChange(roomIndex, value as number)"
            />
          </div>

          <!-- Children dropdown -->
          <div>
            <label
              :for="`children-${roomIndex}`"
              class="block text-sm font-medium text-gray-700 mb-2"
            >
              {{ $t('ibe.common.children', 'Children') }}
            </label>
            <BaseSelect
              :id="`children-${roomIndex}`"
              :model-value="getChildrenCount(room)"
              :options="childrenOptions"
              :disabled="disabled"
              wrapper-class="w-full"
              @update:model-value="(value) => handleChildrenChange(roomIndex, value as number)"
            />
          </div>

          <!-- Children ages -->
          <div v-if="getChildrenCount(room) > 0" class="mt-5">
            <p class="text-sm font-medium text-gray-700 mb-3">
              {{ $t('ibe.room-selector.children-age-departure', 'Children ages at departure') }}
            </p>
            <div class="grid grid-cols-2 gap-3">
              <div
                v-for="(child, childIndex) in getChildren(room)"
                :key="`child-${childIndex}`"
                class="space-y-1"
              >
                <label :for="`child-age-${roomIndex}-${childIndex}`" class="text-sm text-gray-600">
                  {{ childIndex + 1 }}. {{ $t('ibe.common.child', 'Child') }}
                </label>
                <BaseSelect
                  :id="`child-age-${roomIndex}-${childIndex}`"
                  :model-value="child.age ?? null"
                  :options="childAgeOptions"
                  :disabled="disabled"
                  placeholder="---"
                  wrapper-class="w-full"
                  @update:model-value="
                    (value) => handleChildAgeChange(roomIndex, childIndex, value as number | null)
                  "
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Error message -->
    <div v-if="customError" class="px-6 py-3 bg-red-50">
      <p class="text-sm text-red-600">{{ customError }}</p>
    </div>

    <!-- Add room button -->
    <div v-if="canAddRoom" class="pt-3 pb-5">
      <BaseButton
        variant="secondary"
        class="!w-full"
        :disabled="disabled || !canAddRoom"
        @click="handleAddRoom"
      >
        {{ $t('ibe.room-selector.add-room', 'Add room +') }}
      </BaseButton>
    </div>

    <!-- Footer -->
    <div class="flex items-center justify-between pt-5 pb-2 border-t">
      <button
        type="button"
        class="flex items-center gap-1 text-black text-sm underline"
        @click="
          () => {
            emit('more-options-click');
            modalStore.openModal(ModalType.MORE_OPTIONS);
          }
        "
      >
        <span class="w-6 h-5 icon icon-info bg-black" aria-hidden="true"> </span>
        <span>{{ $t('ibe.common.more-options', 'More options') }}</span>
      </button>

      <BaseButton variant="primary" :disabled="disabled" class="!px-5" @click="handleConfirm">
        {{ $t('ibe.common.apply', 'Apply') }}
      </BaseButton>
    </div>
  </div>
</template>
